import { createSelector } from '@reduxjs/toolkit';

import { Context, ConversationSource } from '@/shared/types/chat';
import { RootState } from '@/shared/types/state';

import { TabId } from '../types/state';
import { groupBy } from '../utils/helper';
import { turnToLegacyNode } from '../utils/utils';

const root = (state: RootState) => state.tryIt;

/** The whole slice, for the simulation preview's snapshot/restore. */
export const selectTryItState = (state: RootState) => root(state);

/** The current mode discriminated union, or `null` when the modal is closed. */
export const selectMode = (state: RootState) => root(state).mode;

/** True when the modal is open in replay mode. */
export const selectIsReplay = createSelector(selectMode, (mode) => mode?.kind === 'replay');

/** True when the modal is open in test mode (the live, send-a-message flow). */
export const selectIsTest = createSelector(selectMode, (mode) => mode?.kind === 'test');

/** Phase of the test machine (`idle` / `sending` / `error`), or `null` outside test mode. */
export const selectTestPhase = createSelector(selectMode, (mode) =>
  mode?.kind === 'test' ? mode.phase : null
);

/** Phase of the replay machine, or `null` outside replay mode. */
export const selectReplayPhase = createSelector(selectMode, (mode) =>
  mode?.kind === 'replay' ? mode.phase : null
);

/**
 * Composer/input disabled flag — derived from the state machine. Replaces the
 * legacy `isInputDisabled` slice field.
 */
export const selectIsInputDisabled = createSelector(selectMode, (mode) => {
  if (!mode) {
    return false;
  }
  if (mode.kind === 'test') {
    return mode.phase !== 'idle';
  }
  return mode.phase !== 'ready';
});

/** True while a brain request is in flight. */
export const selectIsSending = createSelector(selectTestPhase, (phase) => phase === 'sending');

/** True while initial replay content is being fetched. */
export const selectIsLoadingReplay = createSelector(
  selectReplayPhase,
  (phase) => phase === 'loading'
);

/** True while a replay translation is in flight. */
export const selectIsTranslating = createSelector(
  selectReplayPhase,
  (phase) => phase === 'translating'
);

/** True when replay loading or translation failed. */
export const selectReplayError = createSelector(selectReplayPhase, (phase) => phase === 'error');

/**
 * The active session id — `tryItSessionId` in test mode, the analytics
 * `sessionId` in replay. `undefined` when the modal is closed.
 */
export const selectTryItSessionId = createSelector(selectMode, (mode) => {
  if (!mode) {
    return undefined;
  }
  return mode.kind === 'test' ? mode.tryItSessionId : mode.sessionId;
});

/**
 * Brain id for the active session — set directly in test mode, resolved from
 * the analytics payload on `replayContentLoaded` for replay. `undefined` until
 * resolved (modal closed, or replay still loading).
 */
export const selectBrainId = createSelector(selectMode, (mode) => mode?.brainId);

/** Replay session start timestamp (replay mode only). */
export const selectSessionStart = createSelector(selectMode, (mode) =>
  mode?.kind === 'replay' ? mode.sessionStart : undefined
);

/** Currently selected replay translation language (replay mode only). */
export const selectReplayLanguage = createSelector(selectMode, (mode) =>
  mode?.kind === 'replay' ? mode.language : undefined
);

/** The canonical conversation timeline — populated by both modes. */
export const selectMessages = (state: RootState) => root(state).messages;

/** True when the timeline has any turns. */
export const selectHasMessages = createSelector(selectMessages, (messages) => messages.length > 0);

/**
 * Contact id carried by the session's turns, if any — the orchestrator writes it
 * into `context.user`. Newest turn wins: a session can get linked mid-conversation.
 */
export const selectContactId = createSelector(selectMessages, (messages) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const contactId = messages[i].metadata.context?.user?.contact_id;
    if (contactId) {
      return contactId;
    }
  }
  return undefined;
});

/**
 * Campaign that produced the session, if any. The id and name come off the turn
 * metadata, `subscriberId` off the context — the two live apart in the payload.
 */
export const selectSessionCampaign = createSelector(selectMessages, (messages) => {
  const campaign = messages.find((message) => message.metadata.campaign?.id)?.metadata.campaign;
  if (!campaign) {
    return undefined;
  }
  let subscriberId: string | undefined;
  for (const message of messages) {
    const id = message.metadata.context?.campaign?.subscriber_id;
    if (typeof id === 'string' && id) {
      subscriberId = id;
      break;
    }
  }
  return { ...campaign, subscriberId };
});

/** Per-turn context snapshots (test mode). */
export const selectContext = (state: RootState): Context[] => root(state).context;

/**
 * The current (editable) context — the last entry in the per-turn stack, which
 * is also what the next test send carries. Empty object when nothing yet.
 */
export const selectCurrentContext = createSelector(
  selectContext,
  (context): Context => context[context.length - 1] ?? {}
);

export const selectSimulationContext = createSelector(
  selectIsReplay,
  selectCurrentContext,
  selectMessages,
  (isReplay, context, messages): Context => {
    if (!isReplay) {
      return context;
    }
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const turnContext = messages[i].metadata.context;
      if (turnContext && Object.keys(turnContext).length > 0) {
        return turnContext;
      }
    }
    return {};
  }
);

/**
 * Name of the bundle whose variables were imported into the current context, or
 * `null` when none is set. The bundle stores its own name under
 * `global.bundle.bundle`; this hides that nesting so the Bundle picker can mark
 * the active option without reaching into the context shape.
 */
export const selectCurrentBundleName = createSelector(selectCurrentContext, (context) => {
  const name = context.global?.bundle?.bundle;
  return typeof name === 'string' ? name : null;
});

/** Tags set on the current (editable) context. Empty array when none. */
export const selectCurrentTags = createSelector(
  selectCurrentContext,
  (context) => context.tags ?? []
);

/** Current selection (node / action / requisite) or `null`. */
export const selectSelection = (state: RootState) => root(state).selection;

/** Index of the selected turn, or `null` when nothing is selected. */
export const selectSelectedTurnIndex = createSelector(
  selectSelection,
  (selection) => selection?.turnIndex ?? null
);

const selectStoredActiveTab = (state: RootState) => root(state).ui.activeTab;

/** The selected turn object, looked up from the timeline by its index. */
export const selectSelectedTurn = createSelector(
  selectMessages,
  selectSelectedTurnIndex,
  (messages, index) => {
    if (index === null) {
      return null;
    }
    return messages[index] ?? null;
  }
);

/**
 * The context to show for the turn in focus. In replay that is the selected
 * turn's own snapshot; in test it is the entry after the selected one, falling
 * back to the newest.
 */
export const selectTurnContext = createSelector(
  selectIsReplay,
  selectSelectedTurn,
  selectContext,
  selectSelectedTurnIndex,
  (isReplay, turn, context, index): Context => {
    if (isReplay) {
      return turn?.metadata.context ?? {};
    }
    return context[(index ?? 0) + 1] ?? context[context.length - 1] ?? {};
  }
);

/** Tags of the turn in focus, from the same context as the variables. */
export const selectTurnTags = createSelector(selectTurnContext, (context) => context.tags ?? []);

export const selectSelectedTurnRequestId = createSelector(
  selectSelectedTurn,
  (turn) => turn?.requestId ?? turn?.metadata.nodesStack?.[0]?.request_id
);

/** Authentication state for the selected turn — feeds the Authentication panel. */
export const selectSelectedTurnAuthentication = createSelector(
  selectSelectedTurn,
  (turn) => turn?.metadata.authentication
);

/** True when the selected turn is an authentication step. */
export const selectHasAuthentication = createSelector(
  selectMessages,
  selectSelectedTurnIndex,
  (messages, index) => {
    if (index === null) {
      return false;
    }
    const selected = messages[index];
    const turn = selected?.pending ? messages[index - 1] : selected;
    return Boolean(turn?.metadata.isAuthentication);
  }
);

/** Currently active right-panel tab. */
export const selectActiveTab = createSelector(
  selectStoredActiveTab,
  selectHasAuthentication,
  (tab, hasAuthentication) =>
    tab === TabId.Authentication && !hasAuthentication ? TabId.Details : tab
);

const firstResponseText = (responses: { type: string; text?: string; url?: string }[]) => {
  const first = responses?.[0];
  if (!first) {
    return undefined;
  }
  return first.type === 'url' ? first.url : first.text;
};

const buildReactionList = (messages: ReturnType<typeof selectMessages>, reaction: '1' | '-1') =>
  messages
    .map((turn, index) => ({
      request_id: turn.requestId,
      reaction: turn.reaction,
      firstMessageText: firstResponseText(turn.responses ?? []),
      nodeIndex: index,
    }))
    .filter((item) => item.reaction === reaction);

/** Positive-reaction turns, formatted for the Review panel. */
export const selectThumbsUp = createSelector(selectMessages, (messages) =>
  buildReactionList(messages, '1')
);

/** Negative-reaction turns, formatted for the Review panel. */
export const selectThumbsDown = createSelector(selectMessages, (messages) =>
  buildReactionList(messages, '-1')
);

/** Where the conversation came from. Drives how responses render. */
export const selectSessionSource = createSelector(
  root,
  (state) => state.source ?? ({} as ConversationSource)
);

/** True once any turn in this conversation has been corrected. */
export const selectIsAmended = (state: RootState) => root(state).isAmended;

export const selectLanguageModelOverride = (state: RootState) => root(state).languageModelOverride;

// ─────────────────────────────────────────────────────────────────────────────
// Legacy `NodeType` views
//
// The reused renderers in `components/` and the Details sections still expect
// the `NodeType` shape. These selectors project the canonical turns into it so
// those components need no change. Remove them when the renderers take a
// `ConversationTurn` directly.
// ─────────────────────────────────────────────────────────────────────────────

export const selectSessionNodes = createSelector(selectMessages, (turns) =>
  turns.map(turnToLegacyNode)
);

export const selectCurrentNode = createSelector(selectSelectedTurn, (turn) =>
  turn ? turnToLegacyNode(turn) : undefined
);

export const selectSelectedMessageNodesStack = createSelector(
  selectSelectedTurn,
  (turn) => turn?.metadata.nodesStack
);

export const selectNodesStackByIndex = (index: number) =>
  createSelector(selectMessages, (turns) => turns[index]?.metadata.nodesStack);

export const selectNodesStackParentNodeNameByIndex = (index: number) =>
  createSelector(selectSessionNodes, (nodes) => {
    const name = nodes[index]?.name;
    if (name === null || name === undefined) {
      return name;
    }

    const nodesStack = nodes[index]?.nodes_stack;

    // For backwards compatibility, if the request_id is not present
    if (!nodesStack?.[0]?.request_id) {
      return name;
    }

    const grouped = groupBy(nodesStack, 'request_id');

    return grouped[grouped.length - 1]?.[0]?.name ?? null;
  });

export const selectStandaloneQuestion = createSelector(
  selectCurrentNode,
  (node) => node?.standalone_question
);

export const selectIsStandaloneAltered = createSelector(
  selectCurrentNode,
  selectStandaloneQuestion,
  (node, standalone) =>
    Boolean(standalone) && standalone !== (node?.messages?.[0] as { text?: string })?.text
);

/** Context is editable in test mode, and only before the first exchange. */
export const selectCanEdit = createSelector(
  selectIsReplay,
  selectContext,
  (isReplay, context) => !isReplay && context.length <= 1
);
